import { Fragment, useMemo, useRef, useState } from "react"

import {
  usePostbackLog,
  usePostbackStats,
  type PostbackLogFilters,
  type PostbackLogRow,
  type PostbackStats,
} from "@/api/hooks/postback"
import { DateRangePicker } from "@/components/DateRangePicker"
import { labelFromIsoRange } from "@/lib/dates"

const STATUS_CHOICES = [
  { value: "", label: "All forward outcomes" },
  { value: "fired", label: "Fired (TikTok 2xx)" },
  { value: "failed", label: "Failed forward" },
  { value: "pending", label: "Pending forward" },
  { value: "skipped", label: "Skipped (no ttclid)" },
] as const

const PAGE_SIZE = 50
type DateMode = "24h" | "today" | "yesterday" | "custom"

function dayBoundsIsoLocal(dayOffset = 0): { from: string; to: string; label: string } {
  const d = new Date()
  d.setDate(d.getDate() + dayOffset)
  const y = d.getFullYear()
  const m = d.getMonth()
  const day = d.getDate()
  const start = new Date(y, m, day, 0, 0, 0, 0)
  const end = new Date(y, m, day, 23, 59, 59, 999)
  const pad = (n: number) => String(n).padStart(2, "0")
  const isoDay = `${y}-${pad(m + 1)}-${pad(day)}`
  return { from: start.toISOString(), to: end.toISOString(), label: isoDay }
}

function fmtDateTime(s: string | null): string {
  if (!s) return "—"
  return new Date(s).toLocaleString()
}

function fmtPercent(v: number | null): string {
  if (v === null) return "—"
  return `${(v * 100).toFixed(1)}%`
}

function StatCard({
  label,
  value,
  hint,
}: {
  label: string
  value: string | number
  hint?: string
}) {
  return (
    <div className="rounded-lg border border-[#4a4a4a] bg-[#3a3a3a] p-4">
      <p className="text-xs uppercase tracking-wide text-white/60">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-white">{value}</p>
      {hint ? <p className="mt-1 text-xs text-white/50">{hint}</p> : null}
    </div>
  )
}

function ForwardBadge({ row }: { row: PostbackLogRow }) {
  let label: string
  let cls: string
  if (row.fired_at && row.http_status && row.http_status >= 200 && row.http_status < 300) {
    label = "fired"
    cls = "bg-green-900/40 text-green-300 border-green-700"
  } else if (row.fired_at) {
    label = "failed"
    cls = "bg-red-900/40 text-red-300 border-red-700"
  } else if (!row.ttclid) {
    label = "skipped"
    cls = "bg-[#555] text-white border-[#666]"
  } else {
    label = "pending"
    cls = "bg-blue-900/40 text-blue-300 border-blue-700"
  }
  return (
    <span className={`inline-block rounded-full border px-2 py-0.5 text-xs font-medium ${cls}`}>
      {label}
    </span>
  )
}

function StatsBar({ data }: { data: PostbackStats | undefined }) {
  if (!data) {
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-[88px] rounded-lg border border-[#4a4a4a] bg-[#3a3a3a]" />
        ))}
      </div>
    )
  }
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
      <StatCard label="Received" value={data.total} hint={`Window ${data.window}`} />
      <StatCard label="Fired" value={data.fired} />
      <StatCard label="Failed" value={data.failed} />
      <StatCard
        label="Success rate"
        value={fmtPercent(data.success_rate)}
        hint="fired / (fired + failed)"
      />
      <StatCard label="Last received" value={fmtDateTime(data.last_received_at)} />
    </div>
  )
}

export default function PostbackTab() {
  const [dateMode, setDateMode] = useState<DateMode>("24h")
  const [forwarded, setForwarded] = useState<string>("")
  const [q, setQ] = useState<string>("")
  const [page, setPage] = useState(0)
  const [expanded, setExpanded] = useState<number | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [customStartDate, setCustomStartDate] = useState<string | null>(null)
  const [customEndDate, setCustomEndDate] = useState<string | null>(null)
  const [customLabel, setCustomLabel] = useState<string>("Custom")
  const customRangeWrapRef = useRef<HTMLDivElement>(null)

  const dateFilter = useMemo(() => {
    if (dateMode === "24h") {
      const now = new Date()
      const from = new Date(now.getTime() - 24 * 60 * 60 * 1000)
      return { date_from: from.toISOString(), date_to: now.toISOString(), label: "24h" }
    }
    if (dateMode === "today") {
      const b = dayBoundsIsoLocal(0)
      return { date_from: b.from, date_to: b.to, label: "Today" }
    }
    if (dateMode === "yesterday") {
      const b = dayBoundsIsoLocal(-1)
      return { date_from: b.from, date_to: b.to, label: "Yesterday" }
    }
    if (customStartDate && customEndDate) {
      const start = new Date(`${customStartDate}T00:00:00`)
      const end = new Date(`${customEndDate}T23:59:59.999`)
      return { date_from: start.toISOString(), date_to: end.toISOString(), label: customLabel }
    }
    const fallback = dayBoundsIsoLocal(0)
    return { date_from: fallback.from, date_to: fallback.to, label: "Custom" }
  }, [dateMode, customStartDate, customEndDate, customLabel])

  const filters: PostbackLogFilters = useMemo(
    () => ({
      forwarded: (forwarded || undefined) as PostbackLogFilters["forwarded"],
      q: q.trim() || undefined,
      date_from: dateFilter.date_from,
      date_to: dateFilter.date_to,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [forwarded, q, page, dateFilter],
  )

  const stats = usePostbackStats({
    date_from: dateFilter.date_from,
    date_to: dateFilter.date_to,
    ...(dateMode === "24h" ? { window: "24h" } : {}),
  })
  const list = usePostbackLog(filters)

  const total = list.data?.total ?? 0
  const rows = list.data?.rows ?? []
  const hasPrev = page > 0
  const hasNext = (page + 1) * PAGE_SIZE < total

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="border-b-2 border-[#8b9aff] pb-2 text-2xl font-semibold text-white">
            Postback Activity
          </h2>
          <p className="mt-1 text-sm text-white/70">
            Roots → TikTok conversion postbacks. Read-only audit log.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(["24h", "today", "yesterday"] as const).map((w) => (
            <button
              key={w}
              onClick={() => {
                setDateMode(w)
                setPage(0)
              }}
              className={`rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
                dateMode === w
                  ? "border-[#8b9aff] bg-[#3a3a3a] text-[#8b9aff]"
                  : "border-[#4a4a4a] bg-[#2d2d2d] text-white hover:bg-[#3a3a3a]"
              }`}
            >
              {w}
            </button>
          ))}
          <div ref={customRangeWrapRef} className="relative">
            <button
              type="button"
              onClick={() => setPickerOpen((v) => !v)}
              className={`rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
                dateMode === "custom"
                  ? "border-[#8b9aff] bg-[#3a3a3a] text-[#8b9aff]"
                  : "border-[#4a4a4a] bg-[#2d2d2d] text-white hover:bg-[#3a3a3a]"
              }`}
            >
              {dateMode === "custom" ? customLabel : "Custom"}
            </button>
            <DateRangePicker
              open={pickerOpen}
              onClose={() => setPickerOpen(false)}
              initialStart={customStartDate}
              initialEnd={customEndDate}
              outsideBoundaryRef={customRangeWrapRef}
              onApply={(s, e, label) => {
                setCustomStartDate(s)
                setCustomEndDate(e)
                setCustomLabel(labelFromIsoRange(s, e) || label)
                setDateMode("custom")
                setPage(0)
                setPickerOpen(false)
              }}
            />
          </div>
        </div>
      </div>

      <StatsBar data={stats.data ? { ...stats.data, window: dateFilter.label as PostbackStats["window"] } : undefined} />

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-[#4a4a4a] bg-[#3a3a3a] p-4">
        <div className="flex-1 min-w-[220px]">
          <label className="mb-1 block text-xs font-medium text-white/70">
            Search transaction or click id
          </label>
          <input
            type="text"
            value={q}
            onChange={(e) => {
              setQ(e.target.value)
              setPage(0)
            }}
            placeholder="tx-…  or  uuid…"
            className="w-full rounded border border-[#555] bg-[#2d2d2d] px-3 py-2 text-sm text-white"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-white/70">Forward outcome</label>
          <select
            value={forwarded}
            onChange={(e) => {
              setForwarded(e.target.value)
              setPage(0)
            }}
            className="rounded border border-[#555] bg-[#2d2d2d] px-3 py-2 text-sm text-white"
          >
            {STATUS_CHOICES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-[#4a4a4a]">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#444] text-white">
            <tr>
              <th className="px-3 py-2 font-medium">Received</th>
              <th className="px-3 py-2 font-medium">Offer</th>
              <th className="px-3 py-2 font-medium">Campaign</th>
              <th className="px-3 py-2 font-medium">Transaction</th>
              <th className="px-3 py-2 font-medium">Click</th>
              <th className="px-3 py-2 font-medium">Roots</th>
              <th className="px-3 py-2 font-medium">Forward</th>
              <th className="px-3 py-2 font-medium">HTTP</th>
              <th className="px-3 py-2 font-medium">Attempts</th>
              <th className="px-3 py-2 font-medium">Payout</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#4a4a4a] text-white">
            {list.isLoading && (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-center text-white/60">
                  Loading…
                </td>
              </tr>
            )}
            {!list.isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-center text-white/60">
                  No postbacks match these filters.
                </td>
              </tr>
            )}
            {rows.map((row) => {
              const isOpen = expanded === row.id
              return (
                <Fragment key={row.id}>
                  <tr
                    onClick={() => setExpanded(isOpen ? null : row.id)}
                    className="cursor-pointer transition-colors hover:bg-[#3a3a3a]"
                  >
                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      {fmtDateTime(row.received_at)}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {row.offer_name ?? `#${row.offer_id}`}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {row.campaign_name ?? (row.campaign_id ? `#${row.campaign_id}` : "—")}
                    </td>
                    <td className="px-3 py-2 text-xs font-mono">{row.transaction_id}</td>
                    <td className="px-3 py-2 text-xs font-mono text-white/70">
                      {row.click_id ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-xs">{row.status}</td>
                    <td className="px-3 py-2">
                      <ForwardBadge row={row} />
                    </td>
                    <td className="px-3 py-2 text-xs">{row.http_status ?? "—"}</td>
                    <td className="px-3 py-2 text-xs">{row.attempts}</td>
                    <td className="px-3 py-2 text-xs">
                      {row.payout !== null ? row.payout.toFixed(2) : "—"}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-[#2d2d2d]">
                      <td colSpan={10} className="px-4 py-3">
                        <div className="grid gap-3 text-xs sm:grid-cols-2">
                          <div className="sm:col-span-2">
                            <p className="text-white/50">ttclid</p>
                            <p className="break-all font-mono text-white [overflow-wrap:anywhere]">
                              {row.ttclid ?? "—"}
                            </p>
                          </div>
                          <div>
                            <p className="text-white/50">Fired at</p>
                            <p className="text-white">{fmtDateTime(row.fired_at)}</p>
                          </div>
                          <div>
                            <p className="text-white/50">TikTok event id</p>
                            <p className="font-mono text-white">{row.tiktok_event_id ?? "—"}</p>
                          </div>
                          <div>
                            <p className="text-white/50">TikTok response code</p>
                            <p className="text-white">{row.tiktok_response_code ?? "—"}</p>
                          </div>
                          <div className="sm:col-span-2">
                            <p className="text-white/50">TikTok response body</p>
                            <pre className="mt-1 max-h-48 overflow-auto rounded border border-[#4a4a4a] bg-[#1f1f1f] p-2 text-[11px] text-white/80">
                              {row.tiktok_response_body ?? "—"}
                            </pre>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-xs text-white/70">
        <span>
          {total === 0
            ? "0 results"
            : `Showing ${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, total)} of ${total}`}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={!hasPrev}
            className="rounded-md border border-[#7a808b]/45 bg-[#636b78] px-3 py-1 text-white shadow-sm transition-colors hover:bg-[#58606c] disabled:opacity-40"
          >
            Prev
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => p + 1)}
            disabled={!hasNext}
            className="rounded-md border border-[#7a808b]/45 bg-[#636b78] px-3 py-1 text-white shadow-sm transition-colors hover:bg-[#58606c] disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  )
}
